import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import React, { ChangeEvent, useState } from 'react';
import Stack from '@mui/material/Stack';
import { useAuth } from 'contexts/auth-context';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import { Box, Checkbox, FormControlLabel, Link, Paper, Typography } from '@mui/material';
import { Spinner } from 'components/spinner';
import { NavUtils, NavPages } from 'utils';

interface Props {
    eventAlias?: string;
    isEventSignup?: boolean;
}

export const Signup = (props: Props) => {
    const [email, setEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState('');
    const [areTermsAccepted, setAreTermsAccepted] = useState(false);
    const { signup } = useAuth();
    const navigate = useNavigate();
    let [searchParams] = useSearchParams();
    const returnUrl = searchParams.get('returnUrl');

    const loginPath = `${NavUtils.generateNavPath(NavPages.Login, undefined, props.eventAlias)}${
        returnUrl ? `?returnUrl=${returnUrl}` : ''
    }`;

    const onSignUpClick = () => {
        var validEmailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
        if (!email.match(validEmailRegex)) {
            return setError('The entered e-mail address is not valid');
        }

        if (password.trim().length < 6) {
            return setError('The password must be at least 6 characters long');
        }

        if (password !== confirmPassword) {
            return setError('Passwords do not match');
        }

        if (!areTermsAccepted) {
            return setError('You must accept our Terms of Service');
        }

        if (!displayName) {
            return setError('Please enter a display name');
        }

        handleSignup();
    };

    const handleSignup = async () => {
        try {
            setError('');
            setLoading(true);
            await signup(email, password, displayName);
            setTimeout(() => {
                navigate(returnUrl ? decodeURI(returnUrl) : NavUtils.generateNavPath(NavPages.Profile, undefined, props.eventAlias));
            }, 1000);
        } catch (error) {
            setError('Failed to create an account');
            console.error('Failed to create an account', error);
        } finally {
            setLoading(false);
        }
    };

    const onTermsAcceptedChenged = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setAreTermsAccepted(checked);
    }

    const handleReset = () => {
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setError('');
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            onSignUpClick();
        }
    };

    const wrapperStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'center'
    };

    const form = (
        <Box sx={wrapperStyle}>
            <Stack spacing={2} direction="column">
                {error && <Alert severity="error">{error}</Alert>}
                <TextField
                    id="txt-display-name"
                    label="Display name"
                    type="text"
                    value={displayName}
                    onKeyDown={handleKeyPress}
                    onChange={(event) => setDisplayName(event.target.value)}
                />
                <TextField id="txt-email" label="Email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
                <TextField
                    type="password"
                    id="txt-password"
                    label="Password"
                    value={password}
                    onKeyDown={handleKeyPress}
                    onChange={(event) => setPassword(event.target.value)}
                />
                <TextField
                    type="password"
                    id="txt-confirm-password"
                    label="Confirm password"
                    value={confirmPassword}
                    onKeyDown={handleKeyPress}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                />
                <FormControlLabel
                    sx={{ mb: 1 }}
                    control={
                        <Checkbox
                            inputProps={{
                                'aria-label': 'By singing up you agree to our Terms of Service'
                            }}
                            onChange={onTermsAcceptedChenged}
                            checked={areTermsAccepted}
                        />
                    }
                    label={
                        <Typography variant="body1" component="span">
                            By singing up you agree to our{' '}
                            <NavLink to="/terms-of-service" target="_blank" rel="noopener noreferrer">
                                Terms of Service
                            </NavLink>
                            .
                        </Typography>
                    }
                />
                <Button variant="contained" disabled={loading || !areTermsAccepted} onClick={onSignUpClick}>
                    Sign Up
                </Button>
                <Button variant="contained" disabled={loading} onClick={handleReset}>
                    Reset form
                </Button>

                <Typography variant="body1" gutterBottom>
                    Already have an account? <Link href={loginPath}>Log In here.</Link>
                </Typography>
            </Stack>
            {loading && <Spinner />}
        </Box>
    );

    if (props.isEventSignup) {
        return form;
    }

    return <Paper className="aq-content" sx={{ boxShadow: 'none' }}>{form}</Paper>;
};
